import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Card,
    CardContent,
    Button,
    Grid,
    FormControl,
    FormControlLabel,
    Radio,
    RadioGroup,
    TextField,
    Divider,
    Chip,
    Stack,
    Slider,
    Switch,
    MenuItem,
    Select,
    InputLabel,
    Tooltip,
    IconButton,
    Alert,
    AlertTitle,
    Collapse,
    FormGroup,
    Checkbox,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Tab,
    Tabs,
    Link,
    CircularProgress,
    Snackbar
} from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';
import {
    Download,
    Wallet,
    Apple,
    Info,
    HelpCircle,
    ChevronDown,
    Mail,
    Send,
    Copy,
    Edit,
    Globe,
    Phone,
    Briefcase,
    FileText,
    CreditCard,
    Smartphone,
    Check,
    ExternalLink,
    MapPin
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { generateWalletPass, emailWalletPass, getPlatformInfo, checkWalletHealth } from './walletApiClient'; // Import the API client

/**
 * Digital Wallet QR Component for creating Apple Wallet and Google Wallet passes
 */
const ProfilePageDigitalWalletQR = ({ 
    profileData, 
    getProfileSlug = () => "demo-user", 
    isTrial = false,
    authToken // Add auth token from your auth context
}) => {
    const navigate = useNavigate();
    
    // Get platform info and set initial wallet type
    const platformInfo = getPlatformInfo();
    
    // State for wallet settings
    const [walletType, setWalletType] = useState(platformInfo.recommendedWallet); // google, apple
    const [walletPassType, setWalletPassType] = useState('business'); // business, event, loyalty
    const [walletDeliveryMethod, setWalletDeliveryMethod] = useState('qr'); // qr, email
    const [walletEmail, setWalletEmail] = useState('');
    const [walletColor, setWalletColor] = useState('#3f51b5');
    const [trackingEnabled, setTrackingEnabled] = useState(false);
    const [includedFields, setIncludedFields] = useState({
        name: true,
        title: true,
        company: true,
        email: true,
        phone: true,
        linkedin: true,
        location: true,
        website: true
    });
    
    // For QR code
    const [qrSize, setQrSize] = useState(200);
    const [qrForeground, setQrForeground] = useState('#000000');
    const [qrBackground, setQrBackground] = useState('#ffffff');
    const [qrShowLogo, setQrShowLogo] = useState(true);
    
    // For API interactions
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [passUrl, setPassUrl] = useState('');
    const [passId, setPassId] = useState('');
    const [walletHealth, setWalletHealth] = useState(null);
    
    const qrCodeRef = useRef(null);
    
    // Demo data if profileData is not provided or incomplete
    const defaultProfile = {
        name: "John Smith",
        title: "Product Manager",
        company: "Example Inc.",
        email: "john@example.com",
        phone: "+1 (555) 123-4567",
        companyUrl: "https://example.com",
        linkedinUrl: "https://linkedin.com/in/johnsmith",
        companyAddress: "123 Main St, San Francisco, CA 94102",
        avatar: "https://i.pravatar.cc/300",
        profileBaseUrl: "profiles.example.com"
    };
    
    // Merge provided data with defaults
    const profile = {
        ...defaultProfile,
        ...profileData
    };
    
    // Helper function to toggle specific field inclusion
    const toggleField = (field) => {
        if (isTrial) return;
        
        setIncludedFields({
            ...includedFields,
            [field]: !includedFields[field]
        });
    };
    
    // Generate wallet pass
    const handleGeneratePass = async () => {
        if (isTrial) {
            navigate('/profiles/upgrade');
            return;
        }
        
        if (!profile.id) {
            setError('Profile ID is required');
            return;
        }
        
        setLoading(true);
        setError('');
        setSuccess('');
        
        try {
            // Get token from localStorage if not passed as prop
            const token = authToken || localStorage.getItem('token');
            
            // Check if we have a valid auth token
            if (!token || token === 'undefined' || token.length < 20) {
                setError('Authentication required. Please log in to generate wallet pass.');
                setLoading(false);
                return;
            }
            
            const options = {
                passType: getPassTypeForBackend(),
                color: walletColor,
                includedFields,
                trackingEnabled
            };
            
            // Platform compatibility check (temporarily disabled for testing)
            // if (walletType === 'apple' && !platformInfo.supportsAppleWallet) {
            //     setError('Apple Wallet is only available on iOS devices');
            //     setLoading(false);
            //     return;
            // }
            
            // Debug: Check token format
            console.log('Auth token prop:', authToken ? `${authToken.substring(0, 20)}...` : 'null');
            console.log('Auth token localStorage:', token ? `${token.substring(0, 20)}...` : 'null');
            console.log('Final token length:', token ? token.length : 0);
            
            // Call the API through our client with wallet type
            const result = await generateWalletPass(profile.id, {
                ...options,
                email: profile.email,
                firstName: profile.firstName || profile.name?.split(' ')[0] || '',
                lastName: profile.lastName || profile.name?.split(' ').slice(1).join(' ') || '',
                logoText: 'KoorAI Pro',
                organizationName: profile.company || 'KoorAI Digital Solutions',
                description: `${profile.name} - Digital Profile Card`
            }, token, walletType);
            
            if (result.success) {
                setPassUrl(result.passUrl || result.downloadUrl);
                setPassId(result.passId);
                setSuccess(`${walletType === 'apple' ? 'Apple' : 'Google'} Wallet pass generated successfully`);
                
                // If email delivery was selected, send the email
                if (walletDeliveryMethod === 'email' && walletEmail) {
                    await handleSendEmail(result.passId);
                }
            } else {
                throw new Error(result.error || 'Failed to generate wallet pass');
            }
        } catch (err) {
            console.error('Error generating wallet pass:', err);
            setError(err.message || 'Failed to generate wallet pass');
        } finally {
            setLoading(false);
        }
    };
    
    // Send wallet pass via email
    const handleSendEmail = async (existingPassId = null) => {
        if (isTrial) {
            navigate('/profiles/upgrade');
            return;
        }
        
        if (!walletEmail) {
            setError('Email address is required');
            return;
        }
        
        if (!profile.id) {
            setError('Profile ID is required');
            return;
        }
        
        setLoading(true);
        setError('');
        
        try {
            // If we don't have a pass ID yet, generate one first
            const usePassId = existingPassId || passId;
            
            if (!usePassId) {
                // Generate a pass first
                await handleGeneratePass();
                return; // handleGeneratePass will call handleSendEmail again with the passId
            }
            
            // Get token from localStorage if not passed as prop
            const token = authToken || localStorage.getItem('token');
            
            // Send the email with the pass ID
            const result = await emailWalletPass(profile.id, walletEmail, usePassId, token, walletType);
            
            if (result.success) {
                setSuccess(`Wallet pass sent to ${walletEmail}`);
            } else {
                throw new Error(result.error || 'Failed to send wallet pass');
            }
        } catch (err) {
            console.error('Error sending wallet pass email:', err);
            setError(err.message || 'Failed to send wallet pass email');
        } finally {
            setLoading(false);
        }
    };
    
    // Copy QR link to clipboard
    const handleCopyLink = () => {
        if (isTrial) {
            navigate('/profiles/upgrade');
            return;
        }
        
        if (!passUrl) {
            handleGeneratePass().then(() => {
                if (passUrl) {
                    navigator.clipboard.writeText(passUrl);
                    setSuccess('Wallet link copied to clipboard');
                }
            });
        } else {
            navigator.clipboard.writeText(passUrl);
            setSuccess('Wallet link copied to clipboard');
        }
    };
    
    // Handle QR code download
    const handleDownloadQR = () => {
        if (isTrial) {
            navigate('/profiles/upgrade');
            return;
        }
        
        if (!passUrl) {
            handleGeneratePass().then(() => {
                if (passUrl) {
                    downloadQRImage();
                }
            });
        } else {
            downloadQRImage();
        }
    };
    
    // Helper function to download the QR code image
    const downloadQRImage = () => {
        try {
            // Get the SVG element
            const svg = document.getElementById('wallet-qr-code');
            if (!svg) {
                throw new Error('QR code element not found');
            }

            // Convert SVG to string
            const svgData = new XMLSerializer().serializeToString(svg);
            
            // Create a canvas element to convert SVG to PNG
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const img = new Image();

            img.onload = () => {
                canvas.width = img.width;
                canvas.height = img.height;
                ctx.drawImage(img, 0, 0);
                
                // Convert canvas to PNG data URL
                const pngUrl = canvas.toDataURL('image/png');
                
                // Create download link
                const downloadLink = document.createElement('a');
                downloadLink.href = pngUrl;
                downloadLink.download = `${profile.name.toLowerCase().replace(/\s+/g, '-')}-${walletType}-wallet-qr.png`;
                document.body.appendChild(downloadLink);
                downloadLink.click();
                document.body.removeChild(downloadLink);
                
                setSuccess('QR code downloaded successfully');
            };

            img.onerror = () => {
                throw new Error('Error loading QR code image');
            };

            // Create a data URL from the SVG
            img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
        } catch (error) {
            console.error('Error downloading QR code:', error);
            setError('Failed to download QR code');
        }
    };
    
    // Get pass type label
    const getPassTypeLabel = () => {
        return walletPassType === 'business'
            ? 'Business Card'
            : walletPassType === 'loyalty'
            ? 'Loyalty Card'
            : 'Event Ticket';
    };
    
    // Convert pass type to backend format
    const getPassTypeForBackend = () => {
        return walletPassType === 'business' ? 'generic' : walletPassType;
    };
    
    // Check wallet health on mount
    useEffect(() => {
        const checkHealth = async () => {
            try {
                const health = await checkWalletHealth();
                setWalletHealth(health);
            } catch (error) {
                console.error('Failed to check wallet health:', error);
                setWalletHealth({ status: 'unknown', error: error.message });
            }
        };
        
        checkHealth();
    }, []);
    
    // Clear notifications after a delay
    useEffect(() => {
        if (success) {
            const timer = setTimeout(() => {
                setSuccess('');
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [success]);
    
    useEffect(() => {
        if (error) {
            const timer = setTimeout(() => {
                setError('');
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [error]);
    
    return (
        <Card elevation={3} sx={{
            borderRadius: 2,
            transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
            '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 8,
            }
        }}>
            <CardContent sx={{ p: 3 }}>
                <Box className="flex justify-between items-center mb-4">
                    <Typography variant="h6" className="font-semibold">
                        Create Digital Wallet Pass
                    </Typography>
                    <Tooltip title="Add your profile to Apple Wallet or Google Wallet">
                        <IconButton size="small">
                            <HelpCircle className="w-4 h-4" />
                        </IconButton>
                    </Tooltip>
                </Box>
                
                {/* Notification area */}
                {error && (
                    <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                        {error}
                    </Alert>
                )}
                
                {success && (
                    <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
                        {success}
                    </Alert>
                )}
                
                <Alert severity="info" sx={{ mb: 3 }}>
                    <AlertTitle>Digital Wallet Integration</AlertTitle>
                    <Typography variant="body2">
                        This component allows users to add your profile to their digital wallet. 
                        Scanning the QR code directs them to a page where they can add your profile 
                        to Apple Wallet or Google Wallet.
                    </Typography>
                    
                    {walletHealth && (
                        <Box sx={{ mt: 2, p: 1, bgcolor: 'background.default', borderRadius: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                                Wallet Services: {walletHealth.status === 'healthy' ? '✅ Online' : '⚠️ ' + walletHealth.status}
                                {walletHealth.services && (
                                    <>
                                        {' | Google: '}{walletHealth.services.google_wallet}
                                        {' | Apple: '}{walletHealth.services.apple_wallet}
                                    </>
                                )}
                            </Typography>
                        </Box>
                    )}
                </Alert>
                
                <Grid container spacing={3}>
                    {/* Wallet Configuration Section */}
                    <Grid item xs={12} md={6}>
                        <Stack spacing={3}>
                            {/* Wallet Type Selection */}
                            <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                    1. Select Wallet Type
                                </Typography>
                                <Tabs
                                    value={walletType}
                                    onChange={(e, newValue) => !isTrial && setWalletType(newValue)}
                                    aria-label="wallet type tabs"
                                    sx={{ mb: 2 }}
                                >
                                    <Tab 
                                        value="google" 
                                        label="Google Wallet" 
                                        icon={<Globe size={16} />} 
                                        iconPosition="start"
                                        disabled={isTrial}
                                    />
                                    <Tab 
                                        value="apple" 
                                        label="Apple Wallet" 
                                        icon={<Apple size={16} />} 
                                        iconPosition="start"
                                        disabled={isTrial}
                                    />
                                </Tabs>
                                
                                <Typography variant="body2" color="text.secondary">
                                    {walletType === 'google'
                                        ? 'Google Wallet works on Android devices and web browsers.'
                                        : 'Apple Wallet works on iPhone, iPad and Apple Watch (also available for testing on other platforms).'}
                                </Typography>
                                
                                {/* Platform detection info */}
                                <Box sx={{ mt: 1, p: 1, bgcolor: 'background.default', borderRadius: 1 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        Detected platform: {platformInfo.platform === 'ios' ? 'iOS' : 'Android/Web'} 
                                        {platformInfo.platform === 'ios' ? ' (Apple Wallet recommended)' : ' (Google Wallet recommended)'}
                                    </Typography>
                                </Box>
                            </Box>
                            
                            {/* Wallet Pass Type */}
                            <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                    2. Select Pass Type
                                </Typography>
                                <FormControl component="fieldset">
                                    <RadioGroup
                                        row
                                        value={walletPassType}
                                        onChange={(e) => !isTrial && setWalletPassType(e.target.value)}
                                    >
                                        <FormControlLabel 
                                            value="business" 
                                            control={<Radio disabled={isTrial} />} 
                                            label="Business Card" 
                                        />
                                        <FormControlLabel 
                                            value="loyalty" 
                                            control={<Radio disabled={isTrial} />} 
                                            label="Loyalty Card" 
                                        />
                                        <FormControlLabel 
                                            value="event" 
                                            control={<Radio disabled={isTrial} />} 
                                            label="Event Pass" 
                                        />
                                    </RadioGroup>
                                </FormControl>
                                
                                <Typography variant="body2" color="text.secondary">
                                    Each pass type has a different layout optimized for its purpose.
                                    {walletType === 'apple' && (
                                        <><br/>Note: Apple Wallet currently supports Generic passes only. Loyalty and Event passes are available for Google Wallet.</>
                                    )}
                                </Typography>
                            </Box>
                            
                            {/* Field Selection */}
                            <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                    3. Select Information to Include
                                </Typography>
                                <FormGroup>
                                    <Grid container spacing={1}>
                                        {Object.entries(includedFields).map(([field, included]) => (
                                            <Grid item xs={6} key={field}>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={included}
                                                            onChange={() => toggleField(field)}
                                                            size="small"
                                                            disabled={isTrial || (field === 'name')}
                                                        />
                                                    }
                                                    label={field.charAt(0).toUpperCase() + field.slice(1)}
                                                />
                                            </Grid>
                                        ))}
                                    </Grid>
                                </FormGroup>
                            </Box>
                            
                            {/* Appearance Settings */}
                            <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                    4. Pass Appearance
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <Typography variant="body2" gutterBottom>
                                            Pass Color
                                        </Typography>
                                        <TextField
                                            type="color"
                                            value={walletColor}
                                            onChange={(e) => !isTrial && setWalletColor(e.target.value)}
                                            size="small"
                                            fullWidth
                                            disabled={isTrial}
                                        />
                                    </Grid>
                                    
                                    <Grid item xs={12}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={qrShowLogo}
                                                    onChange={(e) => !isTrial && setQrShowLogo(e.target.checked)}
                                                    disabled={isTrial}
                                                />
                                            }
                                            label="Include Logo in Pass"
                                        />
                                    </Grid>
                                    
                                    <Grid item xs={12}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={trackingEnabled}
                                                    onChange={(e) => {
                                                        if (isTrial) {
                                                            navigate('/profiles/upgrade');
                                                        } else {
                                                            setTrackingEnabled(e.target.checked);
                                                        }
                                                    }}
                                                    disabled={isTrial}
                                                />
                                            }
                                            label={
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    Track Pass Usage
                                                    <Tooltip title="Get analytics on how many times your pass is viewed">
                                                        <Info size={16} />
                                                    </Tooltip>
                                                </Box>
                                            }
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                            
                            {/* Delivery Method */}
                            <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                    5. Delivery Method
                                </Typography>
                                <FormControl component="fieldset">
                                    <RadioGroup
                                        row
                                        value={walletDeliveryMethod}
                                        onChange={(e) => !isTrial && setWalletDeliveryMethod(e.target.value)}
                                    >
                                        <FormControlLabel 
                                            value="qr" 
                                            control={<Radio disabled={isTrial} />} 
                                            label="QR Code" 
                                        />
                                        <FormControlLabel 
                                            value="email" 
                                            control={<Radio disabled={isTrial} />} 
                                            label="Email" 
                                        />
                                    </RadioGroup>
                                </FormControl>
                                
                                {walletDeliveryMethod === 'email' && (
                                    <TextField
                                        label="Recipient Email"
                                        placeholder="recipient@example.com"
                                        value={walletEmail}
                                        onChange={(e) => !isTrial && setWalletEmail(e.target.value)}
                                        fullWidth
                                        margin="normal"
                                        disabled={isTrial || loading}
                                        InputProps={{
                                            startAdornment: <Mail size={16} style={{ marginRight: 8, opacity: 0.7 }} />,
                                        }}
                                    />
                                )}
                            </Box>
                            
                            {/* Action Buttons */}
                            <Box>
                                {walletDeliveryMethod === 'email' ? (
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        startIcon={loading ? <CircularProgress size={24} color="inherit" /> : <Send />}
                                        onClick={() => handleSendEmail()}
                                        fullWidth
                                        disabled={isTrial || !walletEmail || loading}
                                    >
                                        {loading ? 'Sending...' : (isTrial ? "Upgrade to Send Pass" : "Send Pass via Email")}
                                    </Button>
                                ) : (
                                    <Stack direction="row" spacing={2}>
                                        <Button
                                            variant="outlined"
                                            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Copy />}
                                            onClick={handleCopyLink}
                                            fullWidth
                                            disabled={isTrial || loading}
                                        >
                                            {loading ? 'Processing...' : (isTrial ? "Premium Feature" : "Copy Link")}
                                        </Button>
                                        
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Download />}
                                            onClick={handleDownloadQR}
                                            fullWidth
                                            disabled={isTrial || loading}
                                        >
                                            {loading ? 'Processing...' : (isTrial ? "Upgrade to Download" : "Download QR")}
                                        </Button>
                                    </Stack>
                                )}
                                
                                {/* Generate Pass Button */}
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Wallet />}
                                    onClick={handleGeneratePass}
                                    fullWidth
                                    sx={{ mt: 2 }}
                                    disabled={isTrial || loading}
                                >
                                    {loading ? 'Generating...' : (isTrial ? "Upgrade to Generate" : "Generate Wallet Pass")}
                                </Button>
                            </Box>
                        </Stack>
                    </Grid>
                    
                    {/* Preview Section */}
                    <Grid item xs={12} md={6}>
                        <Stack spacing={3}>
                            {/* QR Code Display */}
                            <Box sx={{ 
                                display: 'flex', 
                                flexDirection: 'column', 
                                alignItems: 'center',
                                position: 'relative'
                            }}>
                                <Typography variant="subtitle2" gutterBottom align="center">
                                    {walletType === 'google' ? 'Google' : 'Apple'} Wallet {getPassTypeLabel()} QR Code
                                </Typography>
                                
                                <Paper 
                                    elevation={3} 
                                    sx={{ 
                                        p: 3, 
                                        bgcolor: 'background.paper', 
                                        borderRadius: 2,
                                        position: 'relative',
                                        minHeight: 240,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: '100%'
                                    }}
                                >
                                    {loading ? (
                                        <CircularProgress />
                                    ) : (
                                        <QRCodeSVG
                                            id="wallet-qr-code"
                                            ref={qrCodeRef}
                                            value={passUrl || `https://example.com/wallet-demo?type=${walletPassType}&color=${encodeURIComponent(walletColor)}`}
                                            size={isTrial ? 200 : qrSize}
                                            bgColor={isTrial ? "#ffffff" : qrBackground}
                                            fgColor={isTrial ? "#000000" : qrForeground}
                                            level="H"
                                            includeMargin={true}
                                            imageSettings={!isTrial && qrShowLogo ? {
                                                src: profile.avatar || "https://i.pravatar.cc/300",
                                                excavate: true,
                                                width: qrSize * 0.25,
                                                height: qrSize * 0.25
                                            } : undefined}
                                        />
                                    )}
                                    
                                    {/* Trial overlay */}
                                    {isTrial && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                bottom: 0,
                                                bgcolor: 'rgba(255, 255, 255, 0.6)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                zIndex: 10,
                                                backdropFilter: 'blur(2px)',
                                                borderRadius: 2,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    bgcolor: 'white',
                                                    p: 2,
                                                    borderRadius: 2,
                                                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                                                    maxWidth: '80%',
                                                    textAlign: 'center'
                                                }}
                                            >
                                                <Typography variant="subtitle2" color="primary" gutterBottom>
                                                    Premium Feature
                                                </Typography>
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    size="small"
                                                    onClick={() => navigate('/profiles/upgrade')}
                                                >
                                                    Upgrade Now
                                                </Button>
                                            </Box>
                                        </Box>
                                    )}
                                </Paper>
                                
                                {passUrl && !isTrial && (
                                    <Box sx={{ mt: 2, textAlign: 'center' }}>
                                        <Button
                                            variant="contained"
                                            color="success"
                                            size="small"
                                            href={passUrl}
                                            target="_blank"
                                            startIcon={<Wallet />}
                                        >
                                            Add to {walletType === 'google' ? 'Google' : 'Apple'} Wallet
                                        </Button>
                                    </Box>
                                )}
                                
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                                    {`Scanning this QR code will let someone add your ${getPassTypeLabel().toLowerCase()} to their ${walletType} Wallet`}
                                </Typography>
                            </Box>
                            
                            {/* Pass Preview */}
                            <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                    Pass Information Preview
                                </Typography>
                                <Paper 
                                    sx={{ 
                                        p: 2, 
                                        bgcolor: 'background.paper', 
                                        borderRadius: 2,
                                        border: `1px solid ${walletColor}`,
                                        position: 'relative',
                                        overflow: 'hidden'
                                    }}
                                >
                                    <Box 
                                        sx={{ 
                                            height: 60, 
                                            bgcolor: walletColor,
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            zIndex: 0
                                        }} 
                                    />
                                    
                                    <Box sx={{ position: 'relative', zIndex: 1, pt: 4 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                            {qrShowLogo && (
                                                <Box 
                                                    component="img" 
                                                    src={profile.avatar || "https://i.pravatar.cc/300"} 
                                                    alt="Profile"
                                                    sx={{ 
                                                        width: 60, 
                                                        height: 60, 
                                                        borderRadius: '50%',
                                                        border: '3px solid white',
                                                        mr: 2,
                                                        bgcolor: 'white'
                                                    }}
                                                />
                                            )}
                                            <Box>
                                                <Typography variant="h6" sx={{ color: 'white' }}>
                                                    {profile.name}
                                                </Typography>
                                                {includedFields.title && (
                                                    <Typography variant="body2" sx={{ color: 'white', opacity: 0.9 }}>
                                                        {profile.title}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                        
                                        <Stack spacing={2} sx={{ mt: 4 }}>
                                            {includedFields.company && (
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <Briefcase size={18} style={{ marginRight: 8 }} />
                                                    <Typography variant="body2">
                                                        {profile.company}
                                                    </Typography>
                                                </Box>
                                            )}
                                            
                                            {includedFields.email && (
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <Mail size={18} style={{ marginRight: 8 }} />
                                                    <Typography variant="body2">
                                                        {profile.email}
                                                    </Typography>
                                                </Box>
                                            )}
                                            
                                            {includedFields.phone && (
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <Phone size={18} style={{ marginRight: 8 }} />
                                                    <Typography variant="body2">
                                                        {profile.phone}
                                                    </Typography>
                                                </Box>
                                            )}
                                            
                                            {includedFields.website && (
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <Globe size={18} style={{ marginRight: 8 }} />
                                                    <Typography variant="body2">
                                                        {profile.companyUrl}
                                                    </Typography>
                                                </Box>
                                            )}
                                            
                                            {includedFields.location && profile.companyAddress && (
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <MapPin size={18} style={{ marginRight: 8 }} />
                                                    <Typography variant="body2">
                                                        {profile.companyAddress}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Stack>
                                        
                                        <Divider sx={{ my: 2 }} />
                                        
                                        <Box sx={{ 
                                            display: 'flex', 
                                            justifyContent: 'center', 
                                            mt: 1 
                                        }}>
                                            <Chip 
                                                label={`${walletType === 'google' ? 'Google' : 'Apple'} Wallet ${getPassTypeLabel()}`}
                                                color="primary"
                                                size="small"
                                                icon={<CreditCard size={16} />}
                                            />
                                        </Box>
                                    </Box>
                                </Paper>
                            </Box>
                            
                            {/* Implementation Resources */}
                            <Accordion>
                                <AccordionSummary expandIcon={<ChevronDown />}>
                                    <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <HelpCircle size={18} />
                                        Implementation Resources
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Typography variant="body2" paragraph>
                                        Learn more about digital wallet passes in our documentation:
                                    </Typography>
                                    
                                    {walletType === 'apple' ? (
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ mt: 2 }} gutterBottom>
                                                Resources:
                                            </Typography>
                                            <Link 
                                                href="https://developer.apple.com/documentation/walletpasses" 
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                                            >
                                                Apple Wallet Developer Documentation
                                                <ExternalLink size={14} />
                                            </Link>
                                        </Box>
                                    ) : (
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ mt: 2 }} gutterBottom>
                                                Resources:
                                            </Typography>
                                            <Link 
                                                href="https://developers.google.com/wallet/generic" 
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}
                                            >
                                                Google Wallet Developer Documentation
                                                <ExternalLink size={14} />
                                            </Link>
                                            
                                            <Link 
                                                href="/help/wallet-integration"
                                                sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                                            >
                                                Our Wallet Integration Guide
                                                <ExternalLink size={14} />
                                            </Link>
                                        </Box>
                                    )}
                                </AccordionDetails>
                            </Accordion>
                        </Stack>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
};

export default ProfilePageDigitalWalletQR;